import i18nFactory from '@yandex-int/i18n';

import { PhoneNumberData } from '../../typings/banners';

import * as keyset from './FormatHelpers.i18n';

const i18n = i18nFactory(keyset);

export interface NumberParts {
    integerPart: string;
    decimalPointSign: string;
    fractionalPart?: string;
}

/**
 * Если числовое поле null, то отображать 0 вместо него некорректно
 */
export function getNumberValue(num: string | null): number | undefined {
    return num !== null ? Number(num) : undefined;
}

/**
 * Обрезает дробную часть дроби и округляет число в большую сторону. Работает для переодических дробей
 */
export function toFixed(num: number, factorialPartLength: number = 0): string {
    const multiplier = Math.pow(10, factorialPartLength);

    return (Math.round(num * multiplier) / multiplier).toFixed(factorialPartLength);
}

const INTEGER_PART_PATTERN = /([0-9](?=(([0-9]{3})+([^0-9]|$))))/g;

export function getNumberParts(num: number, factorialPartLength?: number): NumberParts {
    const decimalPointSign = '.';
    let numStr;

    if (isNaN(num)) {
        throw Error('Num is not a number');
    }

    if (typeof factorialPartLength === 'number' && factorialPartLength >= 0) {
        numStr = toFixed(num, factorialPartLength);
    } else {
        numStr = num.toString();
    }

    const numberParts = numStr.split(decimalPointSign);
    let integerPart = numberParts[0];
    const fractionalPart = numberParts[1];

    integerPart = integerPart.replace(INTEGER_PART_PATTERN, '$1 ');

    return { integerPart, decimalPointSign: ',', fractionalPart };
}

/**
 * Пытается превратить переданную строку(пользовательский ввод) в число
 */
export function parseNumber(numberString: string): number {
    // заменяем потенциальные варианты точки на точку
    numberString = numberString.replace(/[\/,.юЮбБ<>]/gi, '.');

    if (!isNaN(Number(numberString))) {
        return Number(numberString);
    }

    const beforeLength = numberString.length;

    // удаляем все символы кроме чисел и .
    numberString = numberString.replace(/[^0-9.]/g, '');

    const afterLength = numberString.length;

    // если парсим строку только из букв, то считаем, что это не число
    if (beforeLength !== 0 && afterLength === 0) {
        return NaN;
    }

    return Number(numberString);
}

export function parseWithRound(numberString: string): number | undefined {
    const num = Math.round(parseNumber(numberString));

    if (!isNaN(num)) {
        return num;
    }

    return;
}

export function parseNullableNumber(numberString: string): number | undefined {
    if (!numberString) {
        return undefined;
    }
    return parseNumber(numberString);
}

export function formatNumber(num: number, factorialPartLength?: number): string {
    const { integerPart, decimalPointSign, fractionalPart } = getNumberParts(num, factorialPartLength);
    const fractionalPartString = fractionalPart ? decimalPointSign + fractionalPart : '';

    return integerPart + fractionalPartString;
}

export function precisionFloor(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision);

    return Math.floor(value * multiplier) / multiplier;
}

export function precisionRound(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision);

    return Math.round(value * multiplier) / multiplier;
}

export enum DivideIndexType {
    thousand = 1,
    million = 2,
    billion = 3,
    trillion = 4,
    quadrillion = 5,
    quintillion = 6
}

export function getStringValueParts(
    num: number
): {
    value: number;
    sign: string;
} {
    let sign = '',
        value = num;

    // Math.log(n)/Math.log(m) -> аналогична записи log по основанию m числа n.
    // Таким образом, мы находим в какую степень надо возвести число 1000, чтобы получить num
    // Полученная степень - определяет степень тысячи, чтобы дать размерности наименование
    const divideIndex = Math.floor(Math.log(num) / Math.log(1000));

    // Чтобы найти цифровую часть для краткой записи, необходимо поделить число на
    // 1000, возведенную в найденную степень, для отделения размерности.

    if (divideIndex > 0) {
        value = precisionRound(num / Math.pow(1000, divideIndex), 2);
    }

    // От сюда 1000 = 1 тысяча 1000000 = 1 миллион и т.д
    switch (divideIndex) {
        case DivideIndexType.thousand:
            sign = i18n('thousand');
            break;
        case DivideIndexType.million:
            sign = i18n('million');
            break;
        case DivideIndexType.billion:
            sign = i18n('billion');
            break;
        case DivideIndexType.trillion:
            sign = i18n('trillion');
            break;
        case DivideIndexType.quadrillion:
            sign = i18n('quadrillion');
            break;
        case DivideIndexType.quintillion:
            sign = i18n('quintillion');
            break;
        default:
            sign = '';
    }

    return {
        value,
        sign
    };
}

/**
 * Преобразует строку в строку с ценой в формате "1 000,00"
 * поразрядный пробел, два знака после разделителя
 * разделитель запятая или точка(delimiterPoint)
 * отображает '' при некорректных значениях цены и значении меньше, чем '0.01'
 * при значении цены >= 10 000 000 000, обрезает целую часть
 */
export function getFormattedPrice(value: string, delimiterPoint?: boolean): string {
    let num = Math.abs(parseNumber(value));

    if (isNaN(num) || num < 0.005) {
        return '';
    }

    const numberPart = getNumberParts(num, 2);

    // обрезает целую часть: '8 888 888 888 77.05' -> '8 888 888 888.05'
    if (numberPart.integerPart.length > 13) {
        num = parseNumber(`${numberPart.integerPart.substr(0, 13)}.${numberPart.fractionalPart}`);
    }

    let formattedPrice = formatNumber(num, 2).toString();

    if (delimiterPoint) {
        formattedPrice = formattedPrice.split(',').join('.');
    }

    return formattedPrice;
}

/**
 * Преобразует номер телефона из PhoneNumberData
 * в формат +7 (777) 777-77-77
 */
export function formatPhone(phone: PhoneNumberData): string {
    const { codeCountry, codeRegion, phoneNumber, extension } = phone;

    if (codeCountry === '' || codeRegion === '' || phoneNumber === '') {
        return '';
    }

    const formatted = `+${codeCountry} (${codeRegion}) ${phoneNumber.replace(/(\d+)(\d{2})(\d{2})/, '$1-$2-$3')}`;

    return extension === '' ? formatted : `${formatted} (${extension})`;
}

export function divideNumberPositions(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
